
import { ResultWithValue } from 'src/models/baseApi.model';
import { AllUsersPaginationSchemaVM, UserCreateSchemaVM, UserSchemaVM, UserSingleSchemaVM, UserSubscribedJobsSchemaVM, UserSubscribedJobTasksSchemaVM } from 'src/models/user.model';
import { TAG, TagId } from '../config/enums';
import { baseApi } from './baseApi.service';

// Define a service using a base URL and expßected endpoints
export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ############################# CREATE USER #############################
    createUser: builder.mutation<void, UserCreateSchemaVM>({
      extraOptions: {
        useToken: true,
      },
      query: (body: UserCreateSchemaVM) => ({
        url: '/1/User',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, _arg) => [{ type: TAG.USER, id: TagId.List }],
    }),
    // ############################# GET ALL USERS #############################
    getAllUsers: builder.query<ResultWithValue<{ data: UserSchemaVM[], message: string, totalRows: number }>, AllUsersPaginationSchemaVM>({
      extraOptions: {
        useToken: true,
      },
      query: ({ skip, take, userNameToSearchOn, showArchived }) => ({
        url: '/1/User',
        method: 'GET',
        params: {
          skip: Number(skip ?? 0) * Number(take ?? 10),
          take: take ?? 10,
          userNameToSearchOn: userNameToSearchOn,
          showArchived: showArchived,
        },
      }),
      providesTags: (_result, _error, _arg) => [{ type: TAG.USER, id: TagId.List }],
    }),
    // ############################# UPDATE USER #############################
    updateUser: builder.mutation<void, { userId: string; user: UserCreateSchemaVM }>({
      extraOptions: {
        useToken: true,
      },
      query: ({ userId, user }) => ({
        url: `/1/User/${userId}`,
        method: 'PATCH',
        body: user,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: TAG.USER, id: arg.userId }, TAG.USER],
    }),
    // ############################# UPDATE USER LAST LOGGED IN #############################
    updateUserLastLoggedIn: builder.mutation<void, string>({
      extraOptions: {
        useToken: true,
      },
      query: (userId) => ({
        url: `/1/User/${userId}`,
        method: 'PATCH',
        body: { 'lastLoggedIn': new Date().toISOString() },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: TAG.USER, id: arg }, TAG.USER],
    }),
    // ############################# ARCHIVE USER #############################
    archiveUser: builder.mutation<void, string>({
      extraOptions: {
        useToken: true,
      },
      query: (userId: string) => ({
        url: '/2.1/User/Archive',
        method: 'PATCH',
        body: {
          isArchived: true,
          userId: userId
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: TAG.USER, id: arg }, TAG.USER],
    }),
    // ############################# UNARCHIVE USER #############################
    unarchiveUser: builder.mutation<void, string>({
      extraOptions: {
        useToken: true,
      },
      query: (userId: string) => ({
        url: '/2.1/User/Archive',
        method: 'PATCH',
        body: {
          isArchived: false,
          userId: userId
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: TAG.USER, id: arg }, TAG.USER],
    }),
    // ############################# GET USER #############################
    getUser: builder.query<ResultWithValue<UserSingleSchemaVM>, string>({
      extraOptions: {
        useToken: true,
      },
      query: (userId: string) => `/1/User/${userId}`,
      providesTags: (_result, _error, id) => [{ type: TAG.USER, id }],
    }),
    // ############################# GET WORKER TASK ##################################
    //leaving any for now because the response is controlled by the backend
    getWorkerTask: builder.query<{ data: any, message: string, totalRows: number }, { jobId: string; id: string, skip: number, take: number, type: 'Task' | 'QA' | 'QA+Improv' | 'Improv QA'| "ReQa" | undefined, taskId: string }>({
      extraOptions: {
        useToken: true,
      },
      query: ({ jobId, id, skip, take, type, taskId }: { jobId: string; id: string, skip: number, take: number, type: 'Task' | 'QA' | 'QA+Improv' | 'Improv QA'| "ReQa" | undefined, taskId: string }) => ({
        url: `/1.0/User/${id}/JobTasks`,
        method: 'GET',
        params: {
          jobId: jobId,
          skip: Number(skip ?? 0) * Number(take ?? 10),
          take: take,
          type: type,
          taskId: taskId
        },
      }),
    }),
    // ############################# STOP USER ##################################
    stopUser: builder.mutation<void, string>({
      extraOptions: {
        useToken: true,
      },
      query: (userId) => ({
        url: `/2/User/${userId}/Stop`,
        method: 'PATCH',
        body: {
          isStopped: true,
          userId: userId
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: TAG.USER, id: arg }, TAG.USER],
    }),
    // ############################# RESUME USER ##################################
    resumeUser: builder.mutation<void, string>({
      extraOptions: {
        useToken: true,
      },
      query: (userId) => ({
        url: `/2/User/${userId}/Stop`,
        method: 'PATCH',
        body: {
          isStopped: false,
          userId: userId
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: TAG.USER, id: arg }, TAG.USER],
    }),
    // ############################# DELETE USER ##################################
    deleteUser: builder.mutation<void, string>({
      extraOptions: {
        useToken: true,
      },
      query: (userId) => ({
        url: `/1/User/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: TAG.USER, id: arg }, TAG.USER],
    }),
    // ############################# RESEND TEMP PASSWORD ##################################
    resendTempPassword: builder.mutation<void, string>({
      extraOptions: {
        useToken: true,
      },
      query: (email) => ({
        url: `/1/user/resendTempPassword`,
        method: 'POST',
        body: {
          email: email,
        },
      }),
    }),
    // ############################# GET ALL USERS ##################################
    getAllUsersV1: builder.query<ResultWithValue<{ data: UserSchemaVM[], message: string, totalRows: number }>, void>({
      extraOptions: {
        useToken: true,
      },
      query: () => ({
        url: '/1/User',
        method: 'GET',
        params: {
          skip: 0,
          take: 1000,
          showArchived: false,
        },
      }),
      providesTags: (_result, _error, _arg) => [{ type: TAG.USER, id: TagId.List }],
    }),
    // ############################# GET JOBS USER IS ON ############################
    getSubscribedJobsForUser: builder.query<ResultWithValue<UserSubscribedJobsSchemaVM[]>, string>({
      extraOptions: {
        useToken: true,
      },
      query: (userId) => `/1/User/${userId}/Jobs`,
    }),
    // ############################# GET WORKER TASKS ##################################
    //leaving any for now because the response is controlled by the backend
    getSubscribedJobTasksForUser: builder.query<{ data: UserSubscribedJobTasksSchemaVM[], message: string, totalRows: number }, { jobId: string; id: string, skip: number, take: number, type: 'Task' | 'QA' | 'QA+Improv' | 'Improv QA'| "ReQa" | undefined }>({
      extraOptions: {
        useToken: true,
      },
      query: ({ jobId, id, skip, take, type }: { jobId: string; id: string, skip: number, take: number, type: 'Task' | 'QA' | 'QA+Improv' | 'Improv QA'| "ReQa" | undefined }) => ({
        url: `/1.0/User/${id}/WorkerJobTasks`,
        method: 'GET',
        params: {
          jobId: jobId,
          skip: Number(skip ?? 0) * Number(take ?? 10),
          take: take,
          type: type
        },
      }),
    }),
    // ############################# GET WORKER QA TASKS ##################################
    //leaving any for now because the response is controlled by the backend
    getSubscribedJobQaTasksForUser: builder.query<{ data: UserSubscribedJobTasksSchemaVM[], message: string, totalRows: number }, { jobId: string; id: string, skip: number, take: number, type: 'Task' | 'QA' | 'QA+Improv' | 'Improv QA'| "ReQa" | undefined }>({
      extraOptions: {
        useToken: true,
      },
      query: ({ jobId, id, skip, take, type }: { jobId: string; id: string, skip: number, take: number, type: 'Task' | 'QA' | 'QA+Improv' | 'Improv QA'| "ReQa" | undefined }) => ({
        url: `/1.0/User/${id}/WorkerJobQaTasks`,
        method: 'GET',
        params: {
          jobId: jobId,
          skip: Number(skip ?? 0) * Number(take ?? 10),
          take: take,
          type: type
        },
      }),
    }),
    // ############################# RE QA JOBTASK ##################################
    reQaJobTask: builder.mutation<void, { id: string, taskId: string , userId: string, workerTaskId: string}>({
      extraOptions: {
        useToken: true,
      },
      query: ({ id, taskId, userId ,workerTaskId}) => ({
        url: `/1.0/User/${id}/ReQaJobTask/${taskId}`,
        method: 'POST',
        params: {
          userId: userId,
          workerTaskId: workerTaskId
        }
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
  useCreateUserMutation,
  useGetAllUsersQuery,
  useUpdateUserMutation,
  useArchiveUserMutation,
  useUnarchiveUserMutation,
  useGetUserQuery,
  useUpdateUserLastLoggedInMutation,
  useGetWorkerTaskQuery,
  useStopUserMutation,
  useResumeUserMutation,
  useDeleteUserMutation,
  useGetAllUsersV1Query,
  useGetSubscribedJobsForUserQuery,
  useLazyGetUserQuery,
  useResendTempPasswordMutation,
  useGetSubscribedJobTasksForUserQuery,
  useGetSubscribedJobQaTasksForUserQuery,
  useReQaJobTaskMutation
} = userApi;
